"use client"

import React from "react"

import { MultiSelect, type MultiSelectOption } from "@coval/ui"

export interface MetricVisibilityOption {
  id: string
  label: string
}

interface MetricVisibilitySelectProps {
  /** All metrics that can be toggled. */
  metrics: MetricVisibilityOption[]
  /** Ids of the metrics currently selected (e.g. shown/visible). */
  value: string[]
  onChange: (selectedMetricIds: string[]) => void
  onOpenChange?: (open: boolean) => void
  disabled?: boolean
  placeholder?: string
  searchable?: boolean
  "aria-label": string
  "data-testid"?: string
}

/**
 * Generic subset selector over a list of metrics. Not coupled to any metric
 * record shape — callers pass normalized {id,label} options — so it can back
 * blind-review visibility, report metric pickers, etc.
 */
export const MetricVisibilitySelect: React.FC<MetricVisibilitySelectProps> = ({
  metrics,
  value,
  onChange,
  onOpenChange,
  disabled = false,
  placeholder = "Select metrics",
  searchable,
  "aria-label": ariaLabel,
  "data-testid": dataTestId,
}) => {
  const options = React.useMemo<MultiSelectOption[]>(
    () =>
      [...metrics]
        .sort((a, b) => a.label.localeCompare(b.label))
        .map((m) => ({ value: m.id, label: m.label })),
    [metrics],
  )

  return (
    <MultiSelect
      options={options}
      value={value}
      onValueChange={onChange}
      onOpenChange={onOpenChange}
      disabled={disabled}
      placeholder={placeholder}
      searchable={searchable ?? metrics.length > 8}
      aria-label={ariaLabel}
      data-testid={dataTestId}
    />
  )
}
